/**
 * MCP-client OAuth — the "come back after login" stash
 * (TARA_PLUGIN_OAUTH2_PLAN.md WS3).
 *
 * The gateway 302s a signed-out browser to `/oauth/authorize?…`. The consent
 * page parks the full OAuth query here, sends the visitor to `/login`, and
 * `App.tsx` brings them straight back once the agency identity resolves. This
 * is the only return-to mechanism the app needs, and it works on the staging
 * cross-site cookie setup because the bearer is in memory after login.
 *
 * sessionStorage on purpose: it survives the login navigation and a full
 * reload (the "Not you?" sign-out reloads the app), dies with the tab, and
 * never holds anything secret — only the OAuth request parameters, which the
 * client already put in the URL bar. Every access is try/catch'd: private
 * windows and blocked site data must degrade to "no pending request".
 */

const KEY = 'adup.mcpOAuth.pending';
const TTL_MS = 10 * 60 * 1000; // matches central-api's authorization-code TTL

interface Pending {
  search: string;
  expires_at: number;
}

/** Park an OAuth query string (with or without the leading `?`). */
export function stashPendingMcpOAuth(search: string): void {
  const clean = search.startsWith('?') ? search.slice(1) : search;
  if (!clean) {
    return;
  }
  try {
    const value: Pending = { search: clean, expires_at: Date.now() + TTL_MS };
    window.sessionStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // storage unavailable — the user simply starts again from Claude
  }
}

/** Whether a live (unexpired) request is parked. Does not consume it. */
export function hasPendingMcpOAuth(): boolean {
  return read() !== null;
}

/** Take the parked query string (without `?`), or null. Consumes it. */
export function consumePendingMcpOAuth(): string | null {
  const pending = read();
  clearPendingMcpOAuth();
  return pending ? pending.search : null;
}

export function clearPendingMcpOAuth(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

function read(): Pending | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<Pending>;
    if (
      typeof parsed?.search !== 'string' ||
      typeof parsed?.expires_at !== 'number' ||
      parsed.expires_at < Date.now()
    ) {
      clearPendingMcpOAuth();
      return null;
    }
    return { search: parsed.search, expires_at: parsed.expires_at };
  } catch {
    return null;
  }
}
